// frontend/src/App.js

import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  HashRouter,
  BrowserRouter,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';

import { TemplateContext } from './context/TemplateContext';
import Sidebar from './components/Sidebar/Sidebar';
import LandingPage from './components/LandingPage/LandingPage';
import MeetingContent from './components/Meeting/MeetingContent';
import MyMeetings from './components/Meeting/MyMeetings';
import Login from './components/Login/Login';
import JournalTemplateList from './components/JournalTemplateList/JournalTemplateList';
import JournalTemplateForm from './components/JournalTemplateForm/JournalTemplateForm';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import MainContent from './components/MainContent/MainContent';
import MySessions from './components/MySessions/MySessions';
import TextToJournal from './components/TextToJournal/TextToJournal';
import UserManagement from './components/UserManagement/UserManagement';
import MeetingTemplateForm from './components/Meeting/MeetingTemplateForm';
import MeetingTemplateList from './components/Meeting/MeetingTemplateList';
import UsageDashboard from './components/UsageDashboard/UsageDashboard';
import GPUMonitoring from './components/GPUMonitoring/GPUMonitoring';
import Clinics from './components/Clinics/Clinics';
import DocumentTemplateList from './components/DocumentTemplateList/DocumentTemplateList';
import DocumentTemplateForm from './components/DocumentTemplateForm/DocumentTemplateForm';
import SupportLauncher from './components/Support/SupportLauncher';
import TicketList from './components/Support/TicketList';
import AdminErrors from './components/AdminErrors/AdminErrors';
import SmartDictation from './components/SmartDictation/SmartDictation';
import JournalEditorWrapper from './components/JournalEditor/JournalEditorWrapper';
import MedicalTermManager from './components/MedicalTerms/MedicalTermManager';
import WhatsNewModal from './components/WhatsNew/WhatsNewModal';

import { ToastContainer } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/theme.css';
import './styles/animations.css';
import './styles/buttons.css';
import './styles/modals.css';
import './styles/forms.css';
import './App.css';
import './cache-breaker-fix.css'; /* Cache breaker v1 - 2024-06-25 */

import { UserProvider, UserContext } from './UserContext';
import { JournalSystemProvider } from './components/JournalSystemContext/JournalSystemContext';
import { startUploader as startLocalUploader, subscribeUploader } from './services/localRecording/uploader';
import { isRecordingBusy } from './services/recordingActivity';

/* ------------------------------------------------------------- */
/* Dynamiskt val mellan HashRouter (Electron) och BrowserRouter  */
/* ------------------------------------------------------------- */
const isElectron =
  typeof window !== 'undefined' &&
  window.process &&
  window.process.versions &&
  !!window.process.versions.electron;

const isFileProtocol = typeof window !== 'undefined' && window.location.protocol === 'file:';

const Router = (isElectron || isFileProtocol) ? HashRouter : BrowserRouter;
/* ------------------------------------------------------------- */

function AppContent() {
  const { user, setUser, loading } = useContext(UserContext);
  const navigate = useNavigate();

  // Lokal-först: starta bakgrundsuppladdaren så fort användaren är inloggad, så
  // att inspelningar som buffrats lokalt (t.ex. medan servern var nere) skickas
  // upp automatiskt — även om användaren öppnar appen på en annan sida en annan dag.
  // Säger uppladdaren att inloggningen gått ut skickas användaren till inloggningen,
  // men aldrig under en inspelning (händelsen kommer igen varje varv tills dess).
  useEffect(() => {
    if (!user) return undefined;
    startLocalUploader();
    return subscribeUploader((ev) => {
      if (ev.type !== 'login_required' || isRecordingBusy()) return;
      localStorage.removeItem('access_token');
      setUser(null);
      navigate('/login');
    });
  }, [user, setUser, navigate]);

  useEffect(() => {
      if (!window.aionDesktop?.isElectron) return;

      const system = user?.clinic?.journal_system;
      if (system === 'PMO' || system === 'TakeCare') {
        window.aionDesktop.startTransfer(system);
        console.log(`Startade överföringsserver för ${system}`);
      } else {
        // inget journalsystem vi stöder => säkerställ att ingen EXE kör
        window.aionDesktop.stopTransfer();
      }

      // 🔙 cleanup – körs när komponenten avmonteras eller när user ändras
      return () => {
        if (window.aionDesktop?.isElectron) {
          window.aionDesktop.stopTransfer();
        }
      };
    }, [user]);

  if (loading) {
    return <div>Laddar...</div>;
  }

  return (
  <>
    <div className="app-container">
      {user && <Sidebar />}
      <div className="main-content">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sessions" element={<PrivateRoute><MySessions /></PrivateRoute>} />
          <Route path="/meeting-content" element={<PrivateRoute><MeetingContent /></PrivateRoute>} />
          <Route path="/my-meetings" element={<MyMeetings />} />
          <Route path="/meetingmallar" element={<MeetingTemplateList />} />
          <Route path="/meetingmallar/create" element={<MeetingTemplateForm />} />
          <Route path="/meetingmallar/edit/:id" element={<MeetingTemplateForm />} />
          <Route path="/journalmallar" element={<PrivateRoute><JournalTemplateList /></PrivateRoute>} />
          <Route path="/journalmallar/create" element={<PrivateRoute><JournalTemplateForm /></PrivateRoute>} />
          <Route path="/journalmallar/edit/:id" element={<PrivateRoute><JournalTemplateForm /></PrivateRoute>} />
          {/* 📄 DOKUMENTMALLAR */}
          <Route path="/documentmallar" element={<PrivateRoute><DocumentTemplateList /></PrivateRoute>} />
          <Route path="/documentmallar/create" element={<PrivateRoute><DocumentTemplateForm /></PrivateRoute> } />
          <Route path="/documentmallar/edit/:id" element={<DocumentTemplateForm />} />
          {/* KALLELSEMALLAR */}
          {/* SMS-MALLAR */}
          <Route path="/main-content" element={<PrivateRoute><MainContent /></PrivateRoute>} />
          <Route path="/text-to-journal" element={<PrivateRoute><TextToJournal /></PrivateRoute>} />
          <Route path="/smart-dictation" element={<PrivateRoute><SmartDictation /></PrivateRoute>} />
          <Route path="/journal-editor" element={<PrivateRoute><JournalEditorWrapper /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute><UserManagement /></PrivateRoute>} />
          <Route path="/clinics" element={<PrivateRoute><Clinics /></PrivateRoute>} />
          <Route path="/usage-dashboard" element={<PrivateRoute><UsageDashboard /></PrivateRoute>} />
          <Route path="/gpu-monitoring" element={<PrivateRoute><GPUMonitoring /></PrivateRoute>} />
          <Route path="/support-tickets" element={<PrivateRoute><TicketList /></PrivateRoute>} />
          <Route path="/admin-errors" element={<PrivateRoute><AdminErrors /></PrivateRoute>} />
          <Route path="/medical-terms" element={<PrivateRoute><MedicalTermManager /></PrivateRoute>} />
          <Route path="/" element={<PrivateRoute><LandingPage /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
    <ToastContainer position="bottom-right" autoClose={3000} />
    {user && <SupportLauncher user={user} />}
    {user && <WhatsNewModal />}
  </>
  );
}

function App() {
  const [templateId, setTemplateId] = React.useState(null);

  return (
    <UserProvider>
      <TemplateContext.Provider value={{ templateId, setTemplateId }}>
        <JournalSystemProvider>
          <Router>
            <AppContent />
          </Router>
        </JournalSystemProvider>
      </TemplateContext.Provider>
    </UserProvider>
  );
}

export default App;
